import type { LinkedPullRequest, PullRequestState } from "@cmmd-forge/contracts";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  cloneElement,
  useCallback,
  useSyncExternalStore,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactElement,
} from "react";

import { cn } from "../lib/utils";
import {
  PULL_REQUEST_STATE_PRESENTATION,
  PullRequestGlyph,
  type PullRequestGlyphIcon,
} from "./pullRequest/pullRequestIcons";
import {
  pullRequestStatusRole,
  readPullRequestCacheUpdates,
  readPullRequestLiveStatus,
  subscribePullRequestCacheUpdates,
  type PullRequestLiveStatus,
} from "./pullRequest/pullRequestLiveStatus";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";

/**
 * The linked pull-request badge machinery, adopted from t3code's
 * `ThreadStatusIndicators.tsx` and adapted to forge's native pull-request model.
 *
 * Two adaptations carry the whole port. Forge links one change request per thread
 * (`LinkedPullRequest` on the thread record), so the stack, chain and multi-link
 * aggregation of upstream's resolver have no counterpart here. And upstream reads a
 * persisted state snapshot off the link, while forge reads the Command query cache the
 * rest of the pull-request suite already populates (`pullRequestLiveStatus`); a cache
 * that has not answered yet shows the same "status pending" badge an unsynced upstream
 * link does.
 *
 * @module
 */

export interface PrStatusIndicator {
  readonly label: string;
  readonly colorClass: string;
  readonly Icon: PullRequestGlyphIcon;
  readonly tooltip: string;
  readonly tooltipLead: string;
  readonly tooltipTitle: string;
  readonly url: string;
}

/**
 * The status a linked pull request shows, from the newest facts the cache holds. `null` means
 * nothing has answered yet, which the badge renders as "status pending".
 */
export function linkedPullRequestSnapshotStatus(
  link: Pick<LinkedPullRequest, "number" | "url" | "repository">,
  status: PullRequestLiveStatus | null,
): PrStatusIndicator | null {
  if (status === null) return null;
  const presentation = PULL_REQUEST_STATE_PRESENTATION[pullRequestStatusRole(status)];
  const title = status.title ?? link.repository;
  const tooltipLead = `PR #${link.number} - ${presentation.label}`;
  return {
    label: `PR ${presentation.label.toLowerCase()}`,
    colorClass: presentation.toneClassName,
    Icon: presentation.Icon,
    tooltip: `${tooltipLead}: ${title}`,
    tooltipLead,
    tooltipTitle: title,
    url: status.url || link.url,
  };
}

/** The linked change request's newest facts, and the badge presentation built from them. */
export interface LinkedThreadPullRequestStatus {
  readonly liveStatus: PullRequestLiveStatus | null;
  readonly indicator: PrStatusIndicator | null;
}

/**
 * The app's QueryClient when the caller renders inside the provider, and
 * undefined where a bare harness renders the hook without one. Reads degrade
 * to "status pending" either way; nothing here mutates the cache.
 */
function useOptionalQueryClient(): QueryClient | undefined {
  try {
    return useQueryClient();
  } catch {
    return undefined;
  }
}

/**
 * Reads the linked pull request's live status without fetching: the Command cache the rest of
 * the suite populates is the only source, and a cache-event subscription keeps the read current
 * when the answer lands after this row rendered.
 */
export function useLinkedThreadPullRequest(
  linkedPullRequest: LinkedPullRequest | null | undefined,
): LinkedThreadPullRequestStatus {
  const queryClient = useOptionalQueryClient();
  const subscribe = useCallback(
    (onChange: () => void) => subscribePullRequestCacheUpdates(queryClient, onChange),
    [queryClient],
  );
  // The subscription is what re-renders this row when a pull-request write
  // lands, so the read below happens again; the value itself is not needed.
  useSyncExternalStore(subscribe, readPullRequestCacheUpdates, () => 0);
  if (linkedPullRequest == null) return { liveStatus: null, indicator: null };
  const { projectId, projectRepositoryId, repository, number } = linkedPullRequest;
  const liveStatus = readPullRequestLiveStatus(queryClient, {
    projectId,
    repository,
    number,
    ...(projectRepositoryId === undefined ? {} : { projectRepositoryId }),
  });
  return {
    liveStatus,
    indicator: linkedPullRequestSnapshotStatus(linkedPullRequest, liveStatus),
  };
}

export interface ThreadPullRequestBadgePresentation {
  readonly Icon: PullRequestGlyphIcon;
  readonly toneClassName: string;
  readonly label: string;
  readonly text: string | number;
}

/** Resolve the complete badge appearance before rendering it in the sidebar or composer. */
export function resolveThreadPullRequestBadgePresentation({
  number,
  url,
  status,
}: {
  readonly number?: number | undefined;
  readonly url?: string | undefined;
  readonly status: PrStatusIndicator | null;
}): ThreadPullRequestBadgePresentation | null {
  if (number === undefined || url === undefined) return null;
  const tooltip = status?.tooltip ?? `PR #${number}, status pending`;
  return {
    Icon: status?.Icon ?? PullRequestGlyph.pullRequest,
    toneClassName: status?.colorClass ?? "text-muted-foreground",
    label: tooltip,
    text: number,
  };
}

/**
 * The linked-PR badge shared by the sidebar row and the composer strip. The badge owns what it
 * shows: the state glyph and number at the meta size, in the state's color. The caller owns the
 * control it sits in through `render` (an anchor, which is what a badge is), and the badge fills
 * in the link behavior: the href, the accessible name, and the click.
 */
export function ThreadPullRequestBadgeControl({
  render,
  number,
  url,
  status,
  onOpenPullRequest,
}: {
  render: ReactElement<Record<string, unknown>>;
  number?: number | undefined;
  url?: string | undefined;
  status: PrStatusIndicator | null;
  onOpenPullRequest: (event: MouseEvent<HTMLElement>) => void;
}) {
  const presentation = resolveThreadPullRequestBadgePresentation({ number, url, status });
  if (presentation === null || url === undefined) return null;
  return (
    <PullRequestBadge
      render={render}
      presentation={presentation}
      url={url}
      onOpenPullRequest={onOpenPullRequest}
    />
  );
}

function PullRequestBadge({
  render,
  presentation,
  url,
  onOpenPullRequest,
}: {
  render: ReactElement<Record<string, unknown>>;
  presentation: ThreadPullRequestBadgePresentation;
  url: string;
  onOpenPullRequest: (event: MouseEvent<HTMLElement>) => void;
}) {
  const control = cloneElement(render, {
    href: url,
    target: "_blank",
    rel: "noopener noreferrer",
    "aria-label": presentation.label,
    onPointerDown: (event: MouseEvent<HTMLElement>) => event.stopPropagation(),
    onClick: onOpenPullRequest,
  } as Partial<AnchorHTMLAttributes<HTMLElement>>);
  return (
    <Tooltip>
      <TooltipTrigger render={control}>
        <span
          className={cn("contents font-normal text-xs tabular-nums", presentation.toneClassName)}
        >
          <presentation.Icon aria-hidden className="size-3 shrink-0" />
          {presentation.text}
        </span>
      </TooltipTrigger>
      <TooltipPopup side="top">{presentation.label}</TooltipPopup>
    </Tooltip>
  );
}

/** One line of a thread's pull-request list, as the hover mini list draws it. */
export interface ThreadPullRequestEntry {
  readonly number: number;
  readonly url: string;
  /** Known for a linked record; a branch-derived pull request carries none of its own. */
  readonly host: string | null;
  readonly repository: string | null;
  /** Known when the cache has answered; null renders the pending glyph. */
  readonly state: PullRequestState | null;
  readonly isDraft: boolean;
  readonly title: string | null;
}

/**
 * The change requests a thread's row can name: the linked record first, then the branch's own
 * pull request when the two are not the same one. The linked entry carries the newest state the
 * cache holds; the branch entry's state comes from git status directly.
 */
export function resolveThreadPullRequestEntries(
  linkedPullRequest: LinkedPullRequest | null | undefined,
  branchPullRequest: {
    readonly number: number;
    readonly url: string;
    readonly state: PullRequestState;
    readonly title: string | null;
  } | null,
  linkedStatus: PullRequestLiveStatus | null,
): ReadonlyArray<ThreadPullRequestEntry> {
  const entries: ThreadPullRequestEntry[] = [];
  if (linkedPullRequest != null) {
    entries.push({
      number: linkedPullRequest.number,
      url: linkedPullRequest.url,
      host: linkedPullRequest.host,
      repository: linkedPullRequest.repository,
      state: linkedStatus?.state ?? null,
      isDraft: linkedStatus?.isDraft ?? false,
      title: linkedStatus?.title ?? null,
    });
  }
  if (
    branchPullRequest !== null &&
    !entries.some((entry) => samePullRequestEntry(entry, branchPullRequest))
  ) {
    entries.push({
      number: branchPullRequest.number,
      url: branchPullRequest.url,
      host: null,
      repository: null,
      state: branchPullRequest.state,
      isDraft: false,
      title: branchPullRequest.title,
    });
  }
  return entries;
}

/**
 * The host's own spelling of a change-request URL collapses to host, repository and number,
 * which is the identity the entry dedupe compares. A URL that does not parse to that shape
 * (Azure nests two segments deeper) answers null and the caller falls back to the number alone.
 */
function pullRequestUrlIdentity(
  url: string,
): { readonly host: string; readonly repository: string; readonly number: number } | null {
  try {
    const parsed = new URL(url);
    // "-" is GitLab's group/project separator and carries no identity of its own.
    const parts = parsed.pathname.split("/").filter((part) => part !== "" && part !== "-");
    if (parts.length !== 4) return null;
    const number = Number.parseInt(parts[3] ?? "", 10);
    if (!Number.isSafeInteger(number) || number <= 0) return null;
    return {
      host: parsed.host.toLowerCase(),
      repository: `${parts[0]}/${parts[1]}`.toLowerCase(),
      number,
    };
  } catch {
    return null;
  }
}

/**
 * Whether a branch-derived entry is the same change request as one already carried: the number
 * for URLs that do not parse, host, repository and number once they do, so the same number on a
 * different repository is retained rather than hidden.
 */
function samePullRequestEntry(
  entry: ThreadPullRequestEntry,
  branch: { readonly number: number; readonly url: string },
): boolean {
  if (entry.number !== branch.number) return false;
  const entryIdentity = pullRequestUrlIdentity(entry.url);
  const branchIdentity = pullRequestUrlIdentity(branch.url);
  if (entryIdentity === null || branchIdentity === null) return true;
  return (
    entryIdentity.host === branchIdentity.host &&
    entryIdentity.repository === branchIdentity.repository
  );
}

/**
 * A miniature of the thread's pull-request list for the row tooltip: one line per change
 * request, state glyph first, then the number, then the title. Upstream's stack indentation has
 * no counterpart here because forge links one change request per thread.
 */
export function ThreadPullRequestsMiniList({
  entries,
}: {
  entries: ReadonlyArray<ThreadPullRequestEntry>;
}) {
  if (entries.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1" data-slot="thread-pull-requests-mini-list">
      {entries.map((entry) => {
        const presentation =
          entry.state === null
            ? null
            : PULL_REQUEST_STATE_PRESENTATION[
                pullRequestStatusRole({ state: entry.state, isDraft: entry.isDraft })
              ];
        const label = entry.title ?? entry.repository ?? null;
        return (
          <li key={entry.url} className="flex min-w-0 items-center gap-2">
            {presentation ? (
              <presentation.Icon
                aria-hidden
                className={cn("size-3 shrink-0", presentation.toneClassName)}
              />
            ) : (
              <PullRequestGlyph.pullRequest
                aria-hidden
                className="size-3 shrink-0 text-muted-foreground"
              />
            )}
            <span className="shrink-0 font-mono tabular-nums">#{entry.number}</span>
            {label !== null ? (
              <span className="min-w-0 truncate text-foreground/75">{label}</span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
